import { Container, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import type { BingoGameModel } from '../../model/bingo/BingoGameModel';
import { BalanceDisplay } from '../../widgets/BalanceDisplay';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface BingoHudCallbacks {
  onBuyIn: () => void;
}

/**
 * The bingo HUD: balance counter (top centre), the pink BUY IN button (below
 * the card, or at viewport centre when no card is up), and a status label.
 *
 * <p>Subscribes to {@link BingoGameModel} for balance changes and game-state
 * events that update the button label / status text.
 */
export class BingoHudComponent extends Container implements SceneComponent {
  private readonly balance: BalanceDisplay;
  private readonly buyInButton: PrimaryButton;
  private readonly statusLabel: Text;
  private readonly subscriptions: Disposable[] = [];

  constructor(
    private readonly model: BingoGameModel,
    private readonly callbacks: BingoHudCallbacks,
  ) {
    super();
    this.balance = new BalanceDisplay(this.model.balance);
    this.buyInButton = new PrimaryButton({
      label: this.buyInLabelDefault(),
      width: 220,
      fillActive: 0xff6bd6,
      textColor: 0xffffff,
      textSize: 18,
      onTap: () => this.callbacks.onBuyIn(),
    });
    this.statusLabel = this.buildStatusLabel();
    this.addChild(this.balance, this.buyInButton, this.statusLabel);

    this.subscriptions.push(
      this.model.events.on('balance-changed', () => {
        this.balance.setAmount(this.model.balance);
      }),
      this.model.events.on('not-enough-coins', () => {
        this.statusLabel.text = `not enough coins — refresh to reset`;
      }),
      this.model.events.on('game-started', () => {
        this.buyInButton.setLabel('⏵ playing');
      }),
      this.model.events.on('game-ended', (event) => {
        this.buyInButton.setLabel(this.buyInLabelDefault());
        if (!this.model.wonThisGame) {
          this.statusLabel.text = `game over — ${event.reason}`;
        }
      }),
      this.model.events.on('number-called', (event) => {
        // Caller widget owns the visual; HUD just reflects progress in the status.
        this.statusLabel.text = `call ${event.callsMade}/${event.maxCalls} · drawn ${event.number}`;
      }),
      this.model.events.on('bingo-won', (event) => {
        this.statusLabel.text = `BINGO — ${event.win.kind} → +${event.winnings}`;
      }),
    );
  }

  /** Layout: balance always pinned to the top; the button and status sit
   * either below the card (when a game is active) or at viewport centre. */
  layout(ctx: SceneLayoutContext): void {
    const { centerX, machineTop, machineBottom, spinButtonY, height } = ctx;

    this.balance.x = centerX;
    this.balance.y = 16;

    if (machineBottom > machineTop) {
      // Card / caller take up the middle; place the action row below them.
      this.statusLabel.x = centerX;
      this.statusLabel.y = machineBottom + 36;
      this.buyInButton.x = centerX;
      this.buyInButton.y = spinButtonY;
    } else {
      // No card up — centre the action row vertically.
      const cy = Math.round(height / 2);
      this.statusLabel.x = centerX;
      this.statusLabel.y = cy + 60;
      this.buyInButton.x = centerX;
      this.buyInButton.y = cy + 110;
    }
  }

  destroy(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions.length = 0;
    this.balance.destroyDisplay();
    super.destroy({ children: true });
  }

  setStatusText(text: string): void {
    this.statusLabel.text = text;
  }

  /** Stage-local position the scene uses to anchor the win burst. */
  get statusPosition(): { x: number; y: number } {
    return { x: this.statusLabel.x, y: this.statusLabel.y };
  }

  private buyInLabelDefault(): string {
    return `BUY IN  ${this.model.buyIn}`;
  }

  private buildStatusLabel(): Text {
    const t = new Text({
      text: `tap BUY IN (${this.model.buyIn}) to start`,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 14,
        fontWeight: '500',
        fill: 0xffffff,
        align: 'center',
      },
    });
    t.anchor.set(0.5);
    t.alpha = 0.75;
    return t;
  }
}
